import { type ProspectContactDto } from "@crowdcoursing/api/openapi";
import { Box, Checkbox, FormControlLabel, Stack, TextField } from "@mui/material";
import { Field, Form } from "react-final-form";

interface ContactFormValues extends Omit<ProspectContactDto, "id" | "prospectId" | "createdAt" | "updatedAt"> {
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  roleTitle: string | null;
  isPrimary: boolean;
  notes: string | null;
}

interface ContactFormProps {
  initialValues?: Partial<ContactFormValues>;
  onSubmit: (values: ContactFormValues) => void;
  disablePrimaryToggle?: boolean;
}

export const ContactForm = ({ initialValues, onSubmit, disablePrimaryToggle }: ContactFormProps) => {
  return (
    <Form<ContactFormValues>
      initialValues={{
        email: null,
        firstName: "",
        isPrimary: false,
        lastName: "",
        notes: null,
        phone: null,
        roleTitle: null,
        ...initialValues,
      }}
      onSubmit={onSubmit}
      render={({ handleSubmit }) => (
        <form id="contact-form" onSubmit={handleSubmit}>
          <Box display="grid" gap={3}>
            {/* Name Fields */}
            <Stack direction="row" spacing={2}>
              <Field name="firstName">
                {({ input }) => (
                  <TextField {...input} label="First Name" required fullWidth placeholder="Enter first name" />
                )}
              </Field>
              <Field name="lastName">
                {({ input }) => <TextField {...input} label="Last Name" fullWidth placeholder="Enter last name" />}
              </Field>
            </Stack>

            {/* Contact Info */}
            <Stack direction="row" spacing={2}>
              <Field name="email">
                {({ input }) => (
                  <TextField {...input} type="email" label="Email" fullWidth placeholder="Enter email address" />
                )}
              </Field>
              <Field name="phone">
                {({ input }) => <TextField {...input} label="Phone" fullWidth placeholder="Enter phone number" />}
              </Field>
            </Stack>

            {/* Role Title */}
            <Field name="roleTitle">
              {({ input }) => <TextField {...input} label="Role/Title" fullWidth placeholder="Enter role or title" />}
            </Field>

            {/* Primary Contact Toggle */}
            {!disablePrimaryToggle && (
              <Field name="isPrimary" type="checkbox">
                {({ input }) => (
                  <FormControlLabel
                    control={<Checkbox {...input} checked={input.checked} />}
                    label="Set as primary contact"
                  />
                )}
              </Field>
            )}

            {/* Notes */}
            <Field name="notes">
              {({ input }) => (
                <TextField
                  {...input}
                  label="Notes"
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add any additional notes about this contact..."
                />
              )}
            </Field>
          </Box>
        </form>
      )}
    />
  );
};
